/**
 * SealBookModal — Sprint 2
 * The closing ritual of a journey. Shows the loose threads this trip left open
 * (planned-but-unvisited stops, gems that never whispered), lets the traveller
 * curate them and add their own, then seals the book.
 *
 * Sober and quiet: no score, no celebration. Sealing is a full stop, not a prize.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { captureOpenThreads, sealTrip } from '../utils/sealManager';
import theme from '../styles/theme';

export default function SealBookModal({ trip, onClose, onSealed }) {
  const { t } = useTranslation('common');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [sealing, setSealing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const suggested = await captureOpenThreads(trip).catch(() => []);
      if (!cancelled) { setThreads(suggested); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [trip]);

  const removeThread = (id) => setThreads(prev => prev.filter(th => th.id !== id));

  const addThread = () => {
    const text = newText.trim();
    if (!text) return;
    setThreads(prev => [
      ...prev,
      { id: `u_${Date.now()}`, cityId: '', text, kind: 'user' },
    ]);
    setNewText('');
  };

  const confirm = async () => {
    setSealing(true);
    try {
      const res = await sealTrip(trip.tripId, threads);
      onSealed?.(res);
    } catch (e) {
      console.error('[SealBookModal] seal failed:', e);
      setSealing(false);
    }
  };

  const kindLabel = (k) =>
    k === 'unvisited' ? t('seal.unvisited')
    : k === 'unwhispered' ? t('seal.unwhispered')
    : null;

  return (
    <div style={S.backdrop} onClick={e => { if (e.target === e.currentTarget && !sealing) onClose?.(); }}>
      <div style={S.sheet}>
        <div style={S.handle} />
        <h2 style={S.title}>{t('seal.sealBook')}</h2>
        <p style={S.prompt}>{t('seal.sealPrompt')}</p>

        <div style={S.section}>
          <div style={S.sectionLabel}>{t('seal.openThreadsTitle')}</div>
          <div style={S.sectionDesc}>{t('seal.openThreadsDesc')}</div>

          {loading ? (
            <div style={S.muted}>…</div>
          ) : threads.length === 0 ? (
            <div style={S.muted}>{t('seal.noThreads')}</div>
          ) : (
            <div style={S.threadList}>
              {threads.map(th => (
                <div key={th.id} style={S.thread}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.threadText}>{th.text}</div>
                    {kindLabel(th.kind) && <div style={S.threadKind}>{kindLabel(th.kind)}</div>}
                  </div>
                  <button type="button" style={S.threadRemove} onClick={() => removeThread(th.id)} aria-label="×">×</button>
                </div>
              ))}
            </div>
          )}

          <div style={S.addRow}>
            <input
              type="text"
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addThread(); }}
              placeholder={t('seal.threadPlaceholder')}
              style={S.input}
            />
            <button type="button" style={S.addBtn} onClick={addThread}>+</button>
          </div>
        </div>

        <div style={S.actions}>
          <button type="button" style={S.cancel} onClick={onClose} disabled={sealing}>
            {t('seal.cancel')}
          </button>
          <button type="button" style={{ ...S.seal, ...(sealing ? S.sealBusy : {}) }} onClick={confirm} disabled={sealing}>
            {sealing ? '…' : t('seal.confirmSeal')}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(26,51,64,0.5)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  sheet: {
    background: theme.cream, width: '100%', maxWidth: 520,
    borderRadius: '20px 20px 0 0', padding: '12px 20px 28px',
    maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 -8px 32px rgba(26,51,64,0.2)',
  },
  handle: { width: 40, height: 4, borderRadius: 2, background: theme.sand, margin: '0 auto 14px' },
  title: { fontSize: 22, fontWeight: 800, color: theme.brownDark, margin: '0 0 6px' },
  prompt: { fontSize: 14, lineHeight: 1.5, color: theme.brown, margin: '0 0 18px' },
  section: { marginBottom: 18 },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.blueDeep, marginBottom: 4 },
  sectionDesc: { fontSize: 13, color: theme.brown, marginBottom: 12, fontStyle: 'italic' },
  muted: { fontSize: 14, color: theme.sand, fontStyle: 'italic', padding: '8px 0' },
  threadList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  thread: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: theme.card, border: `1px solid ${theme.bluePale}`,
    borderRadius: 10, padding: '10px 12px',
  },
  threadText: { fontSize: 14, color: theme.brownDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  threadKind: { fontSize: 11, color: theme.blue, marginTop: 2 },
  threadRemove: {
    flexShrink: 0, width: 26, height: 26, borderRadius: '50%', border: 'none',
    background: theme.creamDeep, color: theme.brown, fontSize: 18, lineHeight: 1, cursor: 'pointer',
  },
  addRow: { display: 'flex', gap: 8 },
  input: {
    flex: 1, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${theme.bluePale}`,
    fontSize: 14, fontFamily: 'inherit', outline: 'none', background: theme.card,
  },
  addBtn: {
    flexShrink: 0, width: 44, borderRadius: 10, border: 'none',
    background: theme.blue, color: '#fff', fontSize: 22, cursor: 'pointer',
  },
  actions: { display: 'flex', gap: 10, marginTop: 8 },
  cancel: {
    flex: 1, padding: '13px', borderRadius: 12, border: `1.5px solid ${theme.sand}`,
    background: 'transparent', color: theme.brown, fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  seal: {
    flex: 2, padding: '13px', borderRadius: 12, border: 'none',
    background: theme.terracotta, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  sealBusy: { opacity: 0.6, cursor: 'default' },
};
